import { useRef, useState, type ReactNode } from "react";
import {
  Share2,
  Download,
  FolderInput,
  MessageCircle,
  MessageSquare,
  Trash2,
  Pencil,
  FilePlus2,
  Layers,
  GitCompare,
  FolderPlus,
  FolderDown,
  Sparkles,
  Eye,
  BadgeCheck,
  Signature,
  type LucideIcon,
} from "lucide-react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { Slot } from "@radix-ui/react-slot";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { ConfirmDialog } from "../molecules/ConfirmDialog";

/**
 * Actions for a document row's right-click menu. Mirrors the floating
 * `BulkActionBar` toolbar so right-click exposes the same capabilities.
 * The caller (DocumentListTable) builds these already bound to the right
 * target — single mode acts on the right-clicked row, multi mode on the
 * whole selection — so this component stays presentational.
 */
export interface DocumentMenuConfig {
  /** True when the right-clicked row is part of a >1 selection. Drives which
   *  items show (bulk subset vs. full single-row set), matching the toolbar. */
  isMulti: boolean;
  /** Selected count, used only for the multi-delete confirmation copy. */
  selectedCount: number;
  /** Single-target filename, shown as a header row; undefined in multi mode. */
  filename?: string;
  /** Whether the single-selected row is a DWG (enables Download Preview PDF). */
  isDwg: boolean;
  /** Exactly two PDFs selected — enables Compare (multi only). */
  canCompare: boolean;
  /** Exactly two processed IFCs selected — enables Compare IFCs. */
  canCompareModels?: boolean;
  /** Exactly two parent DWGs selected — enables Compare drawings (multi only). */
  canCompareDrawings?: boolean;
  /** Capability gates — undefined handler hides the item (same rule as the toolbar). */
  onShare?: () => void;
  onDownload?: () => void;
  onMoveCopy?: () => void;
  onAskFranklin?: () => void;
  /** The hosting page's own action (the Brief page's "Add appendix"). */
  hostAction?: { label: string; icon: LucideIcon; onClick: () => void };
  onCompare?: () => void;
  onDiffOverview?: () => void;
  onCompareDrawings?: () => void;
  onDelete?: () => void;
  /** Single-row only. */
  onRename?: () => void;
  onAddNewVersion?: () => void;
  /** The row already has versions: "Add new version" reads "Versioning". */
  opensVersioning?: boolean;
  /** Single-row only: open the comments panel on this document. */
  onComments?: () => void;
  /** Single-row only: open the parent DWG in the drawing viewer. */
  onViewDrawing?: () => void;
  /** Single-row only: open the file in the document viewer. */
  onOpen?: () => void;
  /** Single-row only: open a signed container's signature panel. */
  onViewSignatures?: () => void;
  /**
   * Toggle the Verified mark. Bound by the caller to the right-clicked row in
   * single mode and to the whole selection in multi mode. Undefined hides the
   * item — same capability-gate rule as every other action here.
   */
  onToggleVerified?: () => void;
  /**
   * Aggregate Verified state of the target(s); decides the label. A mixed
   * selection offers "Mark as verified", so one click makes it uniform.
   */
  verifiedState?: "all" | "none" | "mixed";
}

/** Actions for a folder row's right-click menu — mirrors `FolderActionMenu`. */
export interface FolderMenuConfig {
  folderName: string;
  onCreateSubfolder?: () => void;
  onExport?: () => void;
  /** Opens the AI organize dialog (WHA-3654). */
  onOrganize?: () => void;
  onShare?: () => void;
  onRename?: () => void;
  onDelete?: () => void;
}

type DocumentRowContextMenuProps = {
  /** The single DOM element the menu attaches to (the row container). */
  children: ReactNode;
  /** When true, render children without a menu (e.g. inline-rename in progress,
   *  or a store-less public share view). Lets the browser's native menu through. */
  disabled?: boolean;
} & (({ variant: "document" } & DocumentMenuConfig) | ({ variant: "folder" } & FolderMenuConfig));

/**
 * Right-click context menu for document and folder rows on /documents and
 * /projects. Wraps a row's container element and exposes the same actions as
 * the existing toolbar (documents) / 3-dots menu (folders), adapting to
 * single- vs. multi-selection. Modeled on the issues page `IssueContextMenu`.
 *
 * Delete confirmation: single-document delete routes through the table's own
 * confirm dialog, so this component only shows its own ConfirmDialog for the
 * multi-document and folder cases (`confirmDelete`).
 */
export function DocumentRowContextMenu(props: DocumentRowContextMenuProps) {
  const { children, disabled } = props;
  const { t } = useLanguage();
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Rename opens the row's inline editor, which confirms on blur. Starting it
  // from the item's onClick lets the still-open menu's focus trap (and the
  // close-time focus restore to the trigger) blur the editor the moment it
  // mounts, silently confirming the unchanged name. Defer the rename to the
  // menu's onCloseAutoFocus and suppress the trigger focus-restore instead —
  // same pattern as FolderActionMenu.
  const renameRequested = useRef(false);

  if (disabled) {
    // No menu of our own, and none from a surrounding surface either (the
    // Folders view's New folder / New document background menu): stop the
    // event here without preventing it, so the browser's native menu still
    // opens — an inline rename input keeps copy and paste.
    return <Slot onContextMenu={(e) => e.stopPropagation()}>{children}</Slot>;
  }

  const isFolder = props.variant === "folder";

  // Folder delete and multi-document delete both need confirmation here.
  // Single-document delete is confirmed by the table's own dialog.
  const confirmDelete = isFolder || (!isFolder && props.isMulti);

  const onRename = props.onRename;
  const handleRenameClick = () => {
    renameRequested.current = true;
  };
  const handleCloseAutoFocus = (e: Event) => {
    if (!renameRequested.current) return;
    renameRequested.current = false;
    e.preventDefault();
    onRename?.();
  };

  const onDelete = props.onDelete;
  const handleDeleteClick = () => {
    if (!onDelete) return;
    if (confirmDelete) setConfirmOpen(true);
    else onDelete();
  };

  let items: ReactNode;
  let deleteLabel: string;
  let confirmTitle: string;
  let confirmDescription: string;

  if (props.variant === "folder") {
    deleteLabel = t.documents.folder.delete;
    confirmTitle = t.documents.folder.delete;
    confirmDescription = t.documents.folder.deleteConfirm;
    items = (
      <>
        {props.onCreateSubfolder && (
          <ContextMenuItem onClick={props.onCreateSubfolder}>
            <FolderPlus className="mr-2 h-4 w-4" />
            {t.documents.folder.createSubfolder}
          </ContextMenuItem>
        )}
        {props.onOrganize && (
          <ContextMenuItem onClick={props.onOrganize}>
            <Sparkles className="mr-2 h-4 w-4" />
            {t.documents.organize.button}
          </ContextMenuItem>
        )}
        {props.onExport && (
          <ContextMenuItem onClick={props.onExport}>
            <FolderDown className="mr-2 h-4 w-4" />
            {t.documents.folder.exportFolder}
          </ContextMenuItem>
        )}
        {props.onShare && (
          <ContextMenuItem onClick={props.onShare}>
            <Share2 className="mr-2 h-4 w-4" />
            {t.documents.folder.shareFolder}
          </ContextMenuItem>
        )}
        {props.onRename && (
          <ContextMenuItem onClick={handleRenameClick}>
            <Pencil className="mr-2 h-4 w-4" />
            {t.documents.folder.rename}
          </ContextMenuItem>
        )}
        {onDelete && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem
              onClick={handleDeleteClick}
              className="text-red-600 focus:bg-red-50 focus:text-red-700"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {deleteLabel}
            </ContextMenuItem>
          </>
        )}
      </>
    );
  } else {
    const ba = t.documents.bulkActions;
    deleteLabel = ba.moveToTrash;
    confirmTitle = ba.confirmTrashTitle;
    confirmDescription = ba.confirmTrashDescription.replace("{count}", String(props.selectedCount));
    items = (
      <>
        {!props.isMulti && props.filename && (
          <>
            <ContextMenuLabel className="truncate font-medium">{props.filename}</ContextMenuLabel>
            <ContextMenuSeparator />
          </>
        )}
        {!props.isMulti && props.onOpen && (
          <ContextMenuItem onClick={props.onOpen}>
            <Eye className="mr-2 h-4 w-4" />
            {t.documents.signedContainer.panel.open}
          </ContextMenuItem>
        )}
        {!props.isMulti && props.onViewSignatures && (
          <ContextMenuItem onClick={props.onViewSignatures}>
            <Signature className="mr-2 h-4 w-4" />
            {t.documents.signedContainer.panel.viewSignatures}
          </ContextMenuItem>
        )}
        {props.onShare && (
          <ContextMenuItem onClick={props.onShare}>
            <Share2 className="mr-2 h-4 w-4" />
            {ba.share}
          </ContextMenuItem>
        )}
        {props.onDownload && (
          <ContextMenuItem onClick={props.onDownload}>
            <Download className="mr-2 h-4 w-4" />
            {ba.download}
          </ContextMenuItem>
        )}
        {props.onMoveCopy && (
          <ContextMenuItem onClick={props.onMoveCopy}>
            <FolderInput className="mr-2 h-4 w-4" />
            {ba.moveCopy}
          </ContextMenuItem>
        )}
        {/* Single-row only actions */}
        {!props.isMulti && props.onRename && (
          <ContextMenuItem onClick={handleRenameClick}>
            <Pencil className="mr-2 h-4 w-4" />
            {ba.rename}
          </ContextMenuItem>
        )}
        {!props.isMulti && props.onAddNewVersion && (
          <ContextMenuItem onClick={props.onAddNewVersion}>
            {props.opensVersioning ? (
              <Layers className="mr-2 h-4 w-4" />
            ) : (
              <FilePlus2 className="mr-2 h-4 w-4" />
            )}
            {props.opensVersioning ? ba.versioning : ba.addNewVersion}
          </ContextMenuItem>
        )}
        {!props.isMulti && props.onComments && (
          <ContextMenuItem onClick={props.onComments}>
            <MessageSquare className="mr-2 h-4 w-4" />
            {ba.comments}
          </ContextMenuItem>
        )}
        {/* View drawing — single parent DWG only. */}
        {!props.isMulti && props.isDwg && props.onViewDrawing && (
          <ContextMenuItem onClick={props.onViewDrawing}>
            <Eye className="mr-2 h-4 w-4" />
            {ba.viewDrawing}
          </ContextMenuItem>
        )}
        {/* Compare PDFs — only when exactly two PDFs are selected (multi). */}
        {props.isMulti && props.canCompare && props.onCompare && (
          <ContextMenuItem onClick={props.onCompare}>
            <GitCompare className="mr-2 h-4 w-4" />
            {ba.comparePdfs}
          </ContextMenuItem>
        )}
        {/* Compare IFCs — data-only diff for two processed IFCs. */}
        {props.isMulti && props.canCompareModels && props.onDiffOverview && (
          <ContextMenuItem onClick={props.onDiffOverview}>
            <GitCompare className="mr-2 h-4 w-4" />
            {ba.diffOverview}
          </ContextMenuItem>
        )}
        {/* Compare drawings — two parent DWGs selected. */}
        {props.isMulti && props.canCompareDrawings && props.onCompareDrawings && (
          <ContextMenuItem onClick={props.onCompareDrawings}>
            <GitCompare className="mr-2 h-4 w-4" />
            {ba.compareDrawings}
          </ContextMenuItem>
        )}
        {props.onToggleVerified && (
          <ContextMenuItem onClick={props.onToggleVerified}>
            <BadgeCheck className="mr-2 h-4 w-4" />
            {props.verifiedState === "all" ? ba.unverify : ba.verify}
          </ContextMenuItem>
        )}
        {props.hostAction && (
          <ContextMenuItem onClick={props.hostAction.onClick}>
            <props.hostAction.icon className="mr-2 h-4 w-4" />
            {props.hostAction.label}
          </ContextMenuItem>
        )}
        {props.onAskFranklin && (
          <ContextMenuItem onClick={props.onAskFranklin}>
            <MessageCircle className="mr-2 h-4 w-4" />
            {ba.askFranklin}
          </ContextMenuItem>
        )}
        {onDelete && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem
              onClick={handleDeleteClick}
              className="text-red-600 focus:bg-red-50 focus:text-red-700"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {deleteLabel}
            </ContextMenuItem>
          </>
        )}
      </>
    );
  }

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
        <ContextMenuContent className="w-56" onCloseAutoFocus={handleCloseAutoFocus}>
          {items}
        </ContextMenuContent>
      </ContextMenu>

      {confirmDelete && onDelete && (
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title={confirmTitle}
          description={confirmDescription}
          confirmLabel={deleteLabel}
          cancelLabel={t.common.cancel}
          variant="destructive"
          onConfirm={() => {
            setConfirmOpen(false);
            onDelete();
          }}
        />
      )}
    </>
  );
}
